/**
 * useReservationActions — orchestrates all reservation mutation hooks.
 *
 * Encapsulates action→mutation dispatch and 409 error routing so the route
 * component stays thin. The caller owns dialog state and passes setActiveDialog/
 * setDialogError callbacks.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ReservationStatus } from '@ooh/contracts';
import {
  useUpgradeHard,
  useCloseSale,
  useCancelReservation,
  useUpdateReservationDates,
  useOverrideReservationStatus,
  useForceRelease,
  useEditExpiryDeadline,
} from '../api/mutations';
import { handleMutationError } from '../api/mutation-helpers';

interface ActionCallbacks {
  onSuccess: () => void;
  onInlineError?: (code: string) => void;
}

export interface ReservationActionContext {
  id: number;
  status: ReservationStatus;
  unitId?: number;
  /**
   * CU-86can6mmu: set by the caller when the reservation belongs to another
   * user AND the current user holds reservation.force_release — mirrors the
   * cancel/force-release/edit-dates force semantics so "Zatwierdź
   * rezerwację"/"Sprzedaj" on other users' rows actually reach the server's
   * force-release-gated path instead of silently 403'ing.
   */
  force?: boolean;
}

// eslint-disable-next-line max-lines-per-function -- hook orchestrates 6 mutations; splitting would obscure the dispatch contract
export function useReservationActions() {
  const { t } = useTranslation('reservations');
  const tMessage = t as (key: string) => string;
  const upgradeHard = useUpgradeHard();
  const closeSale = useCloseSale();
  const cancelReservation = useCancelReservation();
  const updateDates = useUpdateReservationDates();
  const overrideStatus = useOverrideReservationStatus();
  const forceRelease = useForceRelease();
  const editExpiryDeadline = useEditExpiryDeadline();

  /* eslint-disable complexity */ // action→mutation fan-out; guard adds 1 branch for type safety; each branch is a distinct business rule
  const dispatch = React.useCallback(
    // eslint-disable-next-line max-lines-per-function -- action→mutation fan-out over 6 mutations; splitting would obscure the dispatch contract
    (
      action: string,
      ctx: ReservationActionContext,
      data: Record<string, unknown> | undefined,
      callbacks: ActionCallbacks,
    ) => {
      const { id, status, unitId, force } = ctx;
      const { onSuccess, onInlineError } = callbacks;
      const errorHandler = (err: unknown) => handleMutationError(err, onInlineError, tMessage);

      if (action === 'upgrade_hard') {
        upgradeHard.mutate(
          { id, expectedStatus: 'SOFT_RESERVATION', unitId, force },
          { onSuccess, onError: errorHandler },
        );
      } else if (action === 'close_sale') {
        if (status !== 'SOFT_RESERVATION' && status !== 'HARD_RESERVATION') {
          return; // close_sale is only valid from SOFT_RESERVATION or HARD_RESERVATION
        }
        closeSale.mutate(
          { id, expectedStatus: status, unitId, force },
          { onSuccess, onError: errorHandler },
        );
      } else if (action === 'cancel') {
        cancelReservation.mutate({ id, unitId, force }, { onSuccess, onError: errorHandler });
      } else if (action === 'edit_dates' && data) {
        updateDates.mutate(
          {
            id,
            dateFrom: data.dateFrom as string,
            dateTo: data.dateTo as string,
            expectedStatus: status,
            force: data.force as boolean | undefined,
            unitId,
          },
          { onSuccess, onError: errorHandler },
        );
      } else if (action === 'override_status' && data) {
        overrideStatus.mutate(
          {
            id,
            targetStatus: data.targetStatus as ReservationStatus,
            reason: data.reason as string,
            ttlExpiresAt: data.ttlExpiresAt as string | undefined,
            unitId,
          },
          { onSuccess, onError: errorHandler },
        );
      } else if (action === 'force_release') {
        forceRelease.mutate({ id, unitId }, { onSuccess, onError: errorHandler });
      } else if (action === 'expiry_deadline' && data) {
        if (status !== 'SOFT_RESERVATION' && status !== 'HARD_RESERVATION') {
          return; // expiry_deadline is only valid from SOFT_RESERVATION or HARD_RESERVATION
        }
        editExpiryDeadline.mutate(
          {
            id,
            expiryDate: data.expiryDate as string,
            reason: data.reason as string,
            expectedStatus: status,
            unitId,
          },
          { onSuccess, onError: errorHandler },
        );
      }
    },
    [
      upgradeHard,
      closeSale,
      cancelReservation,
      updateDates,
      overrideStatus,
      forceRelease,
      editExpiryDeadline,
      tMessage,
    ],
  );
  /* eslint-enable complexity */

  const isPending =
    upgradeHard.isPending ||
    closeSale.isPending ||
    cancelReservation.isPending ||
    updateDates.isPending ||
    overrideStatus.isPending ||
    forceRelease.isPending ||
    editExpiryDeadline.isPending;

  return { dispatch, isPending };
}
